import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { rupee, apiError, imgSrc, placeholderFor } from "@/lib/api";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { ShoppingCart, IndianRupee, Clock, CheckCircle2, XCircle, AlertTriangle, Crown, Plus, Trash2, Tag, ShieldCheck, Sparkles, Wallet, Rocket, PartyPopper, Trophy, ImagePlus, Boxes, BarChart3, ArrowRight } from "lucide-react";
import { EmptyState, Spinner } from "@/components/States";
import { loadRazorpay } from "@/lib/razorpay";
import { toast } from "sonner";
import { AddFromCatalogue, AddNewProduct } from "@/pages/retailer/RetailerCatalogue";
import CatalogPicker from "@/pages/retailer/CatalogPicker";
import RetailerShopStory from "@/pages/retailer/RetailerShopStory";

const NEXT_ACTION = {
  placed: { label: "Accept", to: "accepted", color: "bg-brand", testid: "retailer-order-accept-btn" },
  accepted: { label: "Start Preparing", to: "preparing", color: "bg-amber-surge" },
  preparing: { label: "Mark Ready", to: "ready", color: "bg-blue-500", testid: "retailer-order-pack-btn" },
};

const daysLeft = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000));
};

export default function RetailerDashboard() {
  const qc = useQueryClient();
  const { data: dash } = useQuery({ queryKey: ["r-dash"], queryFn: () => api.get("/retailer/dashboard").then((r) => r.data) });
  const store = dash?.store;
  const [tab, setTab] = useState("orders");

  const toggleStore = async () => { const { data } = await api.patch("/retailer/store/toggle"); qc.invalidateQueries({ queryKey: ["r-dash"] }); toast.success(data.is_accepting_orders ? "Store is now open" : "Store closed"); };

  return (
    <DashboardShell title="Retailer Dashboard" roleColor="#3B82F6" roleLabel="Retailer"
      headerRight={store && (
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-1.5">
          <span className="text-xs font-semibold text-slate-600">{store.is_accepting_orders ? "Open" : "Closed"}</span>
          <Switch data-testid="retailer-toggle-store-status" checked={store.is_accepting_orders} onCheckedChange={toggleStore} />
        </div>
      )}>
      {!dash ? <div className="flex justify-center py-20"><Spinner className="h-8 w-8" /></div> : (
        <>
          <SubscriptionBanner dash={dash} />
          <FirstSaleNudge dash={dash} />
          <OnboardingChecklist dash={dash} onAddProducts={() => setTab("inventory")} onStoreToggle={toggleStore} />
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Today's Orders" value={dash.todays_orders} icon={ShoppingCart} color="#3B82F6" />
            <StatCard label="Today's Sales" value={rupee(dash.todays_sales)} icon={IndianRupee} color="#059669" />
            <StatCard label="Pending" value={dash.pending} icon={Clock} color="#F59E0B" />
            <StatCard label="Completed" value={dash.completed} icon={CheckCircle2} color="#059669" />
          </div>

          <QuickActions onNav={setTab} />

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4 flex-wrap">
              <TabsTrigger value="orders" data-testid="retailer-tab-orders">Orders</TabsTrigger>
              <TabsTrigger value="inventory" data-testid="retailer-tab-inventory">Products & Inventory</TabsTrigger>
              <TabsTrigger value="coupons" data-testid="retailer-tab-coupons">Coupons</TabsTrigger>
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="analytics" data-testid="retailer-tab-analytics">Analytics</TabsTrigger>
              <TabsTrigger value="story" data-testid="retailer-tab-story">Shop Story</TabsTrigger>
            </TabsList>
            <TabsContent value="story"><RetailerShopStory /></TabsContent>
            <TabsContent value="orders"><RetailerOrders /></TabsContent>
            <TabsContent value="inventory"><RetailerInventory /></TabsContent>
            <TabsContent value="coupons"><RetailerCoupons /></TabsContent>
            <TabsContent value="subscription"><RetailerSubscription /></TabsContent>
            <TabsContent value="analytics"><RetailerAnalytics dash={dash} /></TabsContent>
          </Tabs>
        </>
      )}
    </DashboardShell>
  );
}

const QUICK_ACTIONS = [
  { label: "Add Product", tab: "inventory", icon: Plus, color: "#059669", testid: "quick-action-add-product" },
  { label: "Manage Inventory", tab: "inventory", icon: Boxes, color: "#3B82F6", testid: "quick-action-inventory" },
  { label: "View Orders", tab: "orders", icon: ShoppingCart, color: "#F59E0B", testid: "quick-action-orders" },
  { label: "Offers & Coupons", tab: "coupons", icon: Tag, color: "#EC4899", testid: "quick-action-coupons" },
  { label: "Analytics", tab: "analytics", icon: BarChart3, color: "#8B5CF6", testid: "quick-action-analytics" },
];

function QuickActions({ onNav }) {
  return (
    <div className="mb-6" data-testid="retailer-quick-actions">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">Quick actions</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {QUICK_ACTIONS.map((a) => (
          <button key={a.label} data-testid={a.testid} onClick={() => onNav(a.tab)}
            className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 text-left transition-all hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: `${a.color}18`, color: a.color }}>
              <a.icon className="h-5 w-5" />
            </span>
            <span className="text-sm font-semibold text-slate-800 group-hover:text-brand">{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function FirstSaleNudge({ dash }) {
  const store = dash?.store || {};
  const completed = dash?.completed || 0;
  const key = `gf_firstsale_${store.id || ""}`;
  const [dismissed, setDismissed] = React.useState(() => localStorage.getItem(key) === "1");
  if (completed < 1 || dismissed) return null;
  return (
    <div data-testid="retailer-first-sale-nudge" className="relative mb-6 overflow-hidden rounded-2xl border border-amber-300/60 bg-gradient-to-r from-amber-soft to-brand-soft p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-surge/20"><PartyPopper className="h-6 w-6 text-amber-warm" /></span>
          <div>
            <p className="font-head text-lg font-black text-slate-900">Your first sale is in! 🎉</p>
            <p className="text-sm text-slate-600">You've completed {completed} order{completed === 1 ? "" : "s"} on Grofkart. Keep the momentum going — restock fast movers and share a coupon to bring customers back.</p>
          </div>
        </div>
        <button data-testid="first-sale-dismiss-btn" onClick={() => { localStorage.setItem(key, "1"); setDismissed(true); }} className="shrink-0 text-slate-400 hover:text-slate-700"><XCircle className="h-5 w-5" /></button>
      </div>
    </div>
  );
}

function OnboardingChecklist({ dash, onAddProducts, onStoreToggle }) {
  const store = dash?.store || {};
  const sub = store.subscription || {};
  const verified = dash?.verification_status === "verified";
  const trialLive = ["trial", "active"].includes(sub.status);
  const hasProducts = (dash?.products_count || 0) > 0;
  const isOpen = store.is_accepting_orders;
  const key = `gf_onboarded_${store.id || ""}`;
  const [dismissed, setDismissed] = React.useState(() => localStorage.getItem(key) === "1");

  // Only surface once the free trial has started (verification + mandate done)
  if (!trialLive || dismissed) return null;

  const steps = [
    { done: verified, label: "Account verified (₹11)", hint: "Your identity is confirmed." },
    { done: trialLive, label: "Free trial started", hint: "Auto-debit set up — you're on the 3-month free trial." },
    { done: hasProducts, label: "Add your first products", hint: "Customers can only order once you list items.", cta: !hasProducts && { text: "Add products", onClick: onAddProducts, testid: "onboarding-add-products-btn" } },
    { done: isOpen, label: "Open your store for orders", hint: "Turn your store online to start receiving orders.", cta: !isOpen && { text: "Open store", onClick: onStoreToggle, testid: "onboarding-open-store-btn" } },
  ];
  const completed = steps.filter((s) => s.done).length;
  const allDone = completed === steps.length;
  const pct = Math.round((completed / steps.length) * 100);

  return (
    <div data-testid="retailer-onboarding-checklist" className="mb-6 rounded-2xl border border-brand/25 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-soft"><Rocket className="h-5 w-5 text-brand" /></span>
          <div>
            <p className="font-head text-lg font-black text-slate-900">{allDone ? "You're live! 🎉" : "Get your store live"}</p>
            <p className="text-sm text-slate-500">{allDone ? "Everything's set up — start selling on Grofkart." : `${completed} of ${steps.length} steps done — finish setup to start selling.`}</p>
          </div>
        </div>
        {allDone && (
          <button data-testid="onboarding-dismiss-btn" onClick={() => { localStorage.setItem(key, "1"); setDismissed(true); }} className="text-slate-300 hover:text-slate-600"><XCircle className="h-5 w-5" /></button>
        )}
      </div>
      <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-brand transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <ul className="space-y-2.5">
        {steps.map((s, i) => (
          <li key={i} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {s.done
                ? <CheckCircle2 className="h-5 w-5 shrink-0 text-brand" />
                : <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-slate-300 text-[10px] font-bold text-slate-400">{i + 1}</span>}
              <div>
                <p className={`text-sm font-semibold ${s.done ? "text-slate-400 line-through" : "text-slate-800"}`}>{s.label}</p>
                {!s.done && <p className="text-xs text-slate-400">{s.hint}</p>}
              </div>
            </div>
            {s.cta && <button data-testid={s.cta.testid} onClick={s.cta.onClick} className="shrink-0 rounded-lg bg-brand px-3.5 py-1.5 text-xs font-bold text-white hover:bg-brand-deep">{s.cta.text}</button>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SubscriptionBanner({ dash }) {
  const qc = useQueryClient();
  const sub = dash?.store?.subscription || {};
  const verified = dash?.verification_status === "verified";
  const status = sub.status;

  const refresh = () => { qc.invalidateQueries({ queryKey: ["r-dash"] }); qc.invalidateQueries({ queryKey: ["r-sub"] }); };

  const verify = async () => {
    try {
      const { data: init } = await api.post("/retailer/verify/create");
      const ok = await loadRazorpay();
      if (!ok) return toast.error("Could not load payment gateway");
      const rzp = new window.Razorpay({
        key: init.key_id, amount: init.amount, currency: "INR", order_id: init.rzp_order_id,
        name: "Grofkart", description: `Account Verification Fee ₹${init.fee}`, theme: { color: "#059669" },
        handler: async (resp) => {
          try { await api.post("/retailer/verify/pay", resp); refresh(); toast.success("Account verified! Now enable auto-debit to start your free trial."); }
          catch (e) { toast.error(apiError(e)); }
        },
      });
      rzp.open();
    } catch (e) { toast.error(apiError(e)); }
  };

  const enableTrial = async () => {
    try {
      const { data: init } = await api.post("/retailer/subscription/mandate/create");
      const ok = await loadRazorpay();
      if (!ok) return toast.error("Could not load payment gateway");
      const rzp = new window.Razorpay({
        key: init.key_id, subscription_id: init.subscription_id,
        name: "Grofkart", description: "Start free trial · ₹2 mandate authentication", theme: { color: "#059669" },
        handler: async (resp) => {
          try { await api.post("/retailer/subscription/mandate/verify", resp); refresh(); toast.success("3-month free trial started! Your store is now live."); }
          catch (e) { toast.error(apiError(e)); }
        },
      });
      rzp.open();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Not verified -> pay ₹11
  if (!verified || status === "unverified") {
    return (
      <Banner testid="retailer-banner-verify" tone="amber" icon={ShieldCheck}
        title="Verify your account to go live"
        text="Pay a one-time ₹11 verification fee to activate your store and unlock your 3-month free trial."
        action={<button data-testid="retailer-verify-btn" onClick={verify} className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white">Pay ₹11 & Verify</button>} />
    );
  }
  // Verified but no mandate/trial yet -> start trial (₹2)
  if (status === "verification_paid" || sub.auto_debit_status !== "active" && status !== "trial" && status !== "active" && status !== "grace") {
    return (
      <Banner testid="retailer-banner-trial-start" tone="brand" icon={Sparkles}
        title="Start your 3-month free trial"
        text="Set up auto-debit (a real ₹2 authenticates the mandate). Your store goes live immediately and ₹599 + GST auto-debits only after the free trial ends."
        action={<button data-testid="retailer-start-trial-btn" onClick={enableTrial} className="rounded-xl bg-brand px-5 py-2.5 text-sm font-bold text-white">Enable Auto-Debit · ₹2</button>} />
    );
  }
  if (status === "trial") {
    const dl = daysLeft(sub.trial_ends_at);
    return (
      <Banner testid="retailer-banner-trial-active" tone="brand" icon={Sparkles}
        title={`Free trial active — ${dl} day${dl === 1 ? "" : "s"} left`}
        text={`Your store is live. Auto-debit of ₹599 + GST begins on ${sub.next_billing_date ? new Date(sub.next_billing_date).toLocaleDateString("en-IN") : "trial end"}.`} />
    );
  }
  if (status === "grace") {
    return (
      <Banner testid="retailer-banner-due" tone="amber" icon={AlertTriangle}
        title="Payment due"
        text="Your last payment failed. Pay now from the Subscription tab to avoid your store being hidden." />
    );
  }
  if (status === "expired" || status === "cancelled") {
    return (
      <Banner testid="retailer-banner-hidden" tone="red" icon={XCircle}
        title="Store hidden — subscription inactive"
        text="Auto-debit could not be completed. Re-enable auto-debit or pay now to restore visibility."
        action={<button data-testid="retailer-reactivate-btn" onClick={enableTrial} className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white">Re-enable Auto-Debit</button>} />
    );
  }
  // active
  return (
    <Banner testid="retailer-banner-active" tone="green" icon={CheckCircle2}
      title="Subscription active"
      text={`Your store is live. Next auto-debit on ${sub.next_billing_date ? new Date(sub.next_billing_date).toLocaleDateString("en-IN") : "-"}.`} />
  );
}

function Banner({ tone, icon: Icon, title, text, action, testid }) {
  const tones = {
    brand: "border-brand/30 bg-brand-soft text-brand",
    amber: "border-amber-300/60 bg-amber-soft text-amber-warm",
    red: "border-red-200 bg-red-50 text-red-600",
    green: "border-brand/30 bg-brand-soft text-brand",
  };
  return (
    <div data-testid={testid} className={`mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-6 w-6 shrink-0" />
        <div>
          <p className="font-head font-bold">{title}</p>
          <p className="mt-0.5 text-sm text-slate-600">{text}</p>
        </div>
      </div>
      {action}
    </div>
  );
}

function RetailerOrders() {
  const qc = useQueryClient();
  const { data: orders } = useQuery({ queryKey: ["r-orders"], queryFn: () => api.get("/retailer/orders").then((r) => r.data), refetchInterval: 5000 });
  const update = async (id, status) => {
    try { await api.patch(`/retailer/orders/${id}/status`, { status }); qc.invalidateQueries({ queryKey: ["r-orders"] }); qc.invalidateQueries({ queryKey: ["r-dash"] }); toast.success(`Order ${status}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!orders) return <Spinner />;
  if (!orders.length) return <EmptyState icon={ShoppingCart} title="No orders yet" subtitle="New orders appear here in real-time." />;
  return (
    <div className="space-y-3">
      {orders.map((o) => {
        const action = NEXT_ACTION[o.status];
        return (
          <div key={o.id} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-head font-bold text-slate-900">#{o.order_no} <span className="ml-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold capitalize text-slate-600">{o.status.replace(/_/g, " ")}</span></p>
                <p className="text-sm text-slate-500">{o.customer_name} · {o.items.length} items · {rupee(o.total)} · {o.payment_method.toUpperCase()}</p>
                <p className="mt-0.5 text-xs text-slate-400">Settlement: {rupee(o.retailer_settlement ?? o.subtotal)} <span className="text-red-400">(− {rupee(o.retailer_delivery_fee ?? 7)} delivery fee)</span></p>
              </div>
              <div className="flex gap-2">
                {action && <button data-testid={action.testid} onClick={() => update(o.id, action.to)} className={`rounded-xl px-4 py-2 text-sm font-bold text-white ${action.color}`}>{action.label}</button>}
                {["placed", "accepted"].includes(o.status) && <button onClick={() => update(o.id, "cancelled")} className="rounded-xl border border-red-200 px-3 py-2 text-sm font-semibold text-red-500">Reject</button>}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {o.items.map((it) => <span key={it.product_id} className="rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-600">{it.name} ×{it.qty}</span>)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RetailerInventory() {
  const qc = useQueryClient();
  const { data: products } = useQuery({ queryKey: ["r-prods"], queryFn: () => api.get("/retailer/products").then((r) => r.data) });
  const [show, setShow] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [chooser, setChooser] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editFocus, setEditFocus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const DEFAULT_IMG = "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400";
  const [form, setForm] = useState({ name: "", brand: "", category: "grocery", unit: "1 pc", mrp: "", price: "", stock: "", image: DEFAULT_IMG });

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (f.size > 5 * 1024 * 1024) return toast.error("Image too large (max 5MB)");
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/upload/image", fd);
      setForm((prev) => ({ ...prev, image: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.name || !form.price) return toast.error("Enter product name and price");
    try { await api.post("/retailer/products", { ...form, mrp: +form.mrp || +form.price, price: +form.price, stock: +form.stock || 0 }); qc.invalidateQueries({ queryKey: ["r-prods"] }); setShow(false); setForm({ name: "", brand: "", category: "grocery", unit: "1 pc", mrp: "", price: "", stock: "", image: DEFAULT_IMG }); toast.success("Product added"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const patch = async (id, body) => { await api.patch(`/retailer/products/${id}`, body); qc.invalidateQueries({ queryKey: ["r-prods"] }); };
  const del = async (id) => { await api.delete(`/retailer/products/${id}`); qc.invalidateQueries({ queryKey: ["r-prods"] }); toast.success("Deleted"); };

  return (
    <div>
      <div className="mb-4 flex flex-wrap justify-end gap-2">
        <button data-testid="retailer-add-product-main-btn" onClick={() => setChooser(!chooser)} className="inline-flex items-center gap-1.5 rounded-xl bg-brand px-4 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" /> Add Product</button>
        <button data-testid="retailer-add-from-catalogue-btn" onClick={() => setCatOpen(true)} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600">Quick catalogue</button>
        <button data-testid="retailer-add-new-product-btn" onClick={() => setNewOpen(true)} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600">Submit new product</button>
        <button data-testid="retailer-add-product-btn" onClick={() => setShow(!show)} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600">Quick add</button>
      </div>
      {chooser && (
        <div className="mb-4 grid gap-3 sm:grid-cols-2" data-testid="add-product-chooser">
          <button onClick={() => { setChooser(false); setShow(true); }} data-testid="chooser-create-new" className="rounded-2xl border border-slate-200 bg-white p-4 text-left hover:border-brand">
            <p className="font-bold text-slate-800">Create New Product</p>
            <p className="text-xs text-slate-500">Enter name, image, MRP and price yourself (existing flow).</p>
          </button>
          <button onClick={() => { setChooser(false); setPickerOpen(true); }} data-testid="chooser-select-catalog" className="rounded-2xl border-2 border-brand bg-brand-soft p-4 text-left">
            <p className="font-bold text-brand">Select from Grofkart Catalog</p>
            <p className="text-xs text-slate-600">Ready-made products with images, MRP & categories. Just set your price and stock.</p>
          </button>
        </div>
      )}
      {pickerOpen && <CatalogPicker onClose={() => setPickerOpen(false)} onDone={() => { setPickerOpen(false); qc.invalidateQueries({ queryKey: ["r-prods"] }); }}
        onEditProduct={(mp) => { setPickerOpen(false); setEditFocus(mp.id); setTimeout(() => document.getElementById(`rp-${mp.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100); }} />}
      {catOpen && <AddFromCatalogue onClose={() => setCatOpen(false)} onDone={() => { setCatOpen(false); qc.invalidateQueries({ queryKey: ["r-prods"] }); }} />}
      {newOpen && <AddNewProduct onClose={() => setNewOpen(false)} onDone={() => setNewOpen(false)} />}
      {show && (
        <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center gap-4">
            <img src={form.image} alt="preview" data-testid="retailer-product-image-preview" className="h-20 w-20 rounded-xl border border-slate-200 object-cover" />
            <div>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-700">
                {uploading ? <Spinner className="h-4 w-4 border-white" /> : <ImagePlus className="h-4 w-4" />} {uploading ? "Uploading..." : "Upload image"}
                <input type="file" accept="image/*" onChange={onImage} data-testid="retailer-product-image-input" className="hidden" />
              </label>
              <p className="mt-1 text-[11px] text-slate-400">JPG / PNG / WEBP · up to 5MB</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="retailer-product-name" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input placeholder="Brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input placeholder="Unit (e.g. 1 kg)" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input placeholder="MRP" type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input placeholder="Price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input placeholder="Stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <button onClick={save} disabled={uploading} data-testid="retailer-product-save-btn" className="mt-3 w-full rounded-lg bg-brand py-2 text-sm font-bold text-white disabled:opacity-60">Save product</button>
        </div>
      )}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {(products || []).map((p) => (
          <div key={p.id} id={`rp-${p.id}`} className={`flex items-center gap-3 border-b border-slate-100 p-3 last:border-0 ${editFocus === p.id ? "bg-brand-soft/60" : ""}`}>
            <img src={imgSrc(p.image, p.category)} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placeholderFor(p.category); }} alt="" className="h-11 w-11 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">{p.name}</p>
              <p className="text-xs text-slate-400">{p.unit} · {p.category}{p.master_id && <span className="ml-1 rounded bg-brand-soft px-1 text-[10px] font-bold text-brand">catalog</span>}{p.stock === 0 && <span className="ml-1 rounded bg-red-50 px-1 text-[10px] font-bold text-red-500">Out of Stock</span>}</p>
            </div>
            {p.stock < 5 && <span className="inline-flex items-center gap-1 rounded-md bg-amber-soft px-2 py-0.5 text-[11px] font-bold text-amber-warm"><AlertTriangle className="h-3 w-3" /> Low</span>}
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-400">₹</span>
              <input type="number" defaultValue={p.price} onBlur={(e) => patch(p.id, { price: +e.target.value })} className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-sm" />
            </div>
            <input type="number" defaultValue={p.stock} onBlur={(e) => patch(p.id, { stock: +e.target.value })} className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-sm" title="Stock" />
            <button onClick={() => del(p.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RetailerCoupons() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["r-coupons"], queryFn: () => api.get("/retailer/coupons").then((r) => r.data) });
  const [form, setForm] = useState({ code: "", discount_type: "fixed", value: "", min_order: "", max_discount: "100" });
  const add = async () => {
    if (!form.code || !form.value) return toast.error("Enter code and value");
    try { await api.post("/retailer/coupons", { ...form, value: +form.value, min_order: +form.min_order || 0, max_discount: +form.max_discount || 100 }); qc.invalidateQueries({ queryKey: ["r-coupons"] }); toast.success("Store coupon created"); setForm({ code: "", discount_type: "fixed", value: "", min_order: "", max_discount: "100" }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/retailer/coupons/${id}`); qc.invalidateQueries({ queryKey: ["r-coupons"] }); toast.success("Deleted"); };
  if (!data) return <Spinner />;
  return (
    <div>
      <p className="mb-3 flex items-center gap-2 text-sm text-slate-500"><Tag className="h-4 w-4 text-brand" /> Store coupons apply only to orders from <b className="mx-1">your store</b>.</p>
      <div className="mb-4 flex flex-wrap items-end gap-2 rounded-2xl border border-slate-200 bg-white p-4">
        <input placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="retailer-coupon-code" className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm"><option value="fixed">Fixed ₹</option><option value="percent">Percent %</option></select>
        <input placeholder="Value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="retailer-coupon-value" className="w-20 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <input placeholder="Min order" type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <button onClick={add} data-testid="retailer-coupon-add-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" /> Add</button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100"><th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Code</th><th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Type</th><th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Value</th><th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Min Order</th><th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Used</th><th></th></tr></thead>
          <tbody>
            {data.map((c) => (
              <tr key={c.id} className="border-b border-slate-50">
                <td className="px-4 py-3 font-mono font-bold text-brand">{c.code}</td>
                <td className="px-4 py-3 capitalize text-slate-500">{c.discount_type}</td>
                <td className="px-4 py-3">{c.discount_type === "fixed" ? rupee(c.value) : `${c.value}%`}</td>
                <td className="px-4 py-3">{rupee(c.min_order)}</td>
                <td className="px-4 py-3 text-slate-500">{c.used_count || 0}/{c.usage_limit}</td>
                <td className="px-4 py-3"><button onClick={() => del(c.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {!data.length && <tr><td className="px-4 py-6 text-center text-slate-400" colSpan={6}>No store coupons yet. Create one above.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RetailerSubscription() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["r-sub"], queryFn: () => api.get("/retailer/subscription").then((r) => r.data) });
  const renew = async () => {
    try {
      const { data: init } = await api.post("/retailer/subscription/pay/create");
      const ok = await loadRazorpay();
      if (!ok) return toast.error("Could not load payment gateway");
      const rzp = new window.Razorpay({
        key: init.key_id, amount: init.amount, currency: "INR", order_id: init.rzp_order_id,
        name: "Grofkart", description: "Monthly Retailer Subscription", theme: { color: "#059669" },
        handler: async (resp) => {
          try {
            await api.post("/retailer/subscription/pay/verify", resp);
            qc.invalidateQueries({ queryKey: ["r-sub"] });
            qc.invalidateQueries({ queryKey: ["r-dash"] });
            toast.success("Subscription renewed for 30 days!");
          } catch (e) { toast.error(apiError(e)); }
        },
      });
      rzp.open();
    } catch (e) { toast.error(apiError(e)); }
  };
  const autoDebit = async () => {
    try {
      const { data: init } = await api.post("/retailer/subscription/mandate/create");
      const ok = await loadRazorpay();
      if (!ok) return toast.error("Could not load payment gateway");
      const rzp = new window.Razorpay({
        key: init.key_id, subscription_id: init.subscription_id,
        name: "Grofkart", description: "Auto-debit ₹599 + GST / month", theme: { color: "#059669" },
        handler: async (resp) => {
          try {
            await api.post("/retailer/subscription/mandate/verify", resp);
            qc.invalidateQueries({ queryKey: ["r-sub"] });
            qc.invalidateQueries({ queryKey: ["r-dash"] });
            toast.success("Auto-debit enabled! Your 3-month free trial is now live.");
          } catch (e) { toast.error(apiError(e)); }
        },
      });
      rzp.open();
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!data) return <Spinner />;
  const sub = data.subscription || {};
  const expiry = sub?.expires_at ? new Date(sub.expires_at).toLocaleDateString("en-IN") : "-";
  const statusColor = sub?.status === "active" || sub?.status === "trial" ? "text-brand" : "text-amber-warm";
  return (
    <div>
      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase text-slate-400">Current plan</p>
            <p className="font-head text-xl font-black text-slate-900">{sub?.plan || "Monthly Retailer Plan"} — {rupee(sub?.amount || 599)}/mo</p>
            <p className="mt-1 text-sm text-slate-500">Status: <span className={`font-semibold capitalize ${statusColor}`}>{sub?.status || "-"}</span> · {sub?.status === "trial" ? "Trial ends" : "Next billing"} {sub?.next_billing_date ? new Date(sub.next_billing_date).toLocaleDateString("en-IN") : expiry}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {sub?.auto_debit_status === "active"
              ? <span data-testid="retailer-autodebit-status" className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1.5 text-xs font-bold text-brand"><CheckCircle2 className="h-3.5 w-3.5" /> Auto-debit ON</span>
              : <button onClick={autoDebit} data-testid="retailer-autodebit-btn" className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white">Enable Auto-Debit</button>}
            <button onClick={renew} data-testid="retailer-pay-subscription-btn" className="rounded-xl bg-brand px-5 py-2.5 text-sm font-bold text-white">Pay &amp; Renew</button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {(data.plans || []).map((p) => (
          <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2"><Crown className="h-5 w-5 text-amber-surge" /><h3 className="font-head font-bold text-slate-900">{p.name}</h3></div>
            <p className="mt-1 font-head text-2xl font-black text-brand">{rupee(p.price)}<span className="text-sm text-slate-400">/mo</span></p>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-600">{p.features.map((f, i) => <li key={i} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand" /> {f}</li>)}</ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function RetailerAnalytics({ dash }) {
  const firstSale = (dash.completed || 0) >= 1;
  return (
    <div>
      {firstSale && (
        <div data-testid="retailer-first-sale-badge" className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-300/60 bg-amber-soft px-4 py-2 text-sm font-bold text-amber-warm">
          <Trophy className="h-4 w-4" /> First Sale Achieved
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Total Sales" value={rupee(dash.total_sales)} icon={IndianRupee} color="#059669" />
        <StatCard label="Net Settlement" value={rupee(dash.net_settlement)} icon={Wallet} color="#3B82F6" />
        <StatCard label="Delivery Fee Deducted" value={rupee(dash.delivery_deductions)} icon={IndianRupee} color="#EF4444" />
        <StatCard label="Low Stock Items" value={dash.low_stock} icon={AlertTriangle} color="#F59E0B" />
      </div>
    </div>
  );
}
